import { useNotificationToast } from '@/lib/hooks/useNotificationToast';
import { useState } from 'react';
import { AppDispatch, RootState } from '../../../../app/redux/configureStore';
import { useAppDispatch, useAppSelector } from '../../../../app/redux/hooks';
import { UserMatch, UserMatchAction } from '../../../../app/redux/reducers/userMatchesReducer';

export const useUserMatches = () => {
  const dispatch = useAppDispatch();
  const { showNotificationToast } = useNotificationToast();
  const { matches, status, showInbox, showMatchSelection } = useAppSelector((state: RootState) => state.matches);
  const totalMatch = matches.length;
  const [currentItemIndex, setCurrentItemIndex] = useState<number | undefined>(undefined);

  const getMatchIndex = (itemId: string) => {
    return matches.findIndex((item: UserMatch) => item.userMatchId === itemId);
  };

  const getCurrentItem = () => {
    if (currentItemIndex === undefined || totalMatch === 0) return null;
    if (currentItemIndex >= 0 && currentItemIndex === totalMatch) {
      return matches[currentItemIndex - 1];
    }
    return matches[currentItemIndex];
  };

  const updateCurrentItemById = (itemId: string) => {
    const itemIndex = getMatchIndex(itemId);
    if (itemIndex !== -1) {
      setCurrentItemIndex(itemIndex);
    }
  };

  const nextItem = () => {
    if (currentItemIndex !== undefined && currentItemIndex >= totalMatch - 1) {
      return;
    }
    setCurrentItemIndex((prevIndex) => (prevIndex !== undefined ? prevIndex + 1 : 0));
  };

  const previousItem = () => {
    if (!currentItemIndex || currentItemIndex < 0) {
      return currentItemIndex;
    }
    setCurrentItemIndex((prevIndex) => (prevIndex !== undefined ? prevIndex - 1 : undefined));
  };

  /**
   * Checks if the specified UserMatchAction CTA (Call-to-Action) is true for the current item.
   * @param {UserMatchAction} action The UserMatchAction being pressed
   * @return {boolean} True if the specified CTA is true for the current item, otherwise false.
   **/
  const isMatchActionTrue = (action: UserMatchAction, match: UserMatch) => {
    if (action === UserMatchAction.Seen) return match.seen;
    if (action === UserMatchAction.Saved) return match.saved;
    else return false;
  };

  /**
   * Check if at least one message in an array of UserMatches matches a certain action type.
   * @param {UserMatch[]} matches - An array of UserMatch objects.
   * @param {'read'|'unread'} action - The action type to check for ('read' or 'unread').
   * @return {boolean} - True if at least one message matches the specified action type, otherwise false.
   */
  const isAnyMessageAction = (matches: UserMatch[], action: 'read' | 'unread') => {
    return matches.some((match) => {
      if (action === 'read') {
        // If action is 'read', return true if at least one match is read.
        return isMatchActionTrue(UserMatchAction.Seen, match);
      } else if (action === 'unread') {
        // If action is 'unread', return true if at least one match is unread.
        return !isMatchActionTrue(UserMatchAction.Seen, match);
      }
      return false;
    });
  };

  /**
   * Handles the transition for the specified UserMatchAction CTA (Call-to-Action).
   * @param {AppDispatch} updateMatches The function to update the matches with the new action.
   * @param {UserMatchAction} action The UserMatchAction being pressed for the transition.
   **/
  const transitionHandler = async (
    updateMatches: AppDispatch,
    action: UserMatchAction,
    match: UserMatch,
    status?: boolean
  ) => {
    // If the action is already true for the current item, no transition is needed.
    // if (action !== UserMatchAction.Saved && isMatchActionTrue(action, match)) return;
    const response = await updateMatches(match, action, status);
    if (action === UserMatchAction.Dismissed) {
      const itemIndex = getMatchIndex(match.userMatchId);
      updateCurrentIndexAfterDelete(itemIndex);
    }
    if (response === false) {
      showNotificationToast({
        type: 'error',
        message: 'Sorry, something went wrong.'
      });
    }
  };

  /**
   * Handles the transition for the specified UserMatchAction CTA (Call-to-Action) for all matches.
   * @param {AppDispatch} updateMatches The function to update the matches with the new action.
   * @param {UserMatchAction} action The UserMatchAction being pressed for the transition.
   **/
  const transitionHandlerAll = async (updateMatches: AppDispatch, action: UserMatchAction, status?: boolean) => {
    const selectedMatches = getAllSelectedMatches();
    await Promise.all(
      selectedMatches.map((match: UserMatch) => transitionHandler(updateMatches, action, match, status))
    );
  };

  const updateCurrentIndexAfterDelete = (deletedIndex: number) => {
    if (currentItemIndex !== undefined && currentItemIndex === deletedIndex) {
      if (deletedIndex === totalMatch - 1) {
        // If the deleted item was the last one, adjust the index to the new last item
        setCurrentItemIndex(deletedIndex - 1);
      } else {
        setCurrentItemIndex(deletedIndex);
      }
    }
  };

  /**
   * Handles the transition for the specified UserMatchAction CTA (Call-to-Action) for all matches.
   * @param {AppDispatch} updateMatches The function to update the matches with the new action.
   * @param {UserMatchAction} action The UserMatchAction being pressed for the transition.
   **/
  const getAllSelectedMatches = () => {
    return matches.filter((match: UserMatch) => match.selected);
  };

  /**
   * reset all necessary states like the current/selected match message
   **/
  const resetInbox = () => {
    setCurrentItemIndex(undefined);
  };

  return {
    showInbox,
    showMatchSelection,
    selectedMatches: getAllSelectedMatches(),
    areAllMessagesAction: isAnyMessageAction,
    matches,
    currentItemIndex,
    resetInbox,
    status,
    numMatches: totalMatch,
    currentItem: getCurrentItem(),
    previousItem,
    nextItem,
    transitionHandler,
    transitionHandlerAll,
    updateCurrentItemById
  };
};
